import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Viewport } from 'pixi-viewport';
import type { FurnitureCatalog } from '../../../models/apiTypes';
import { updateFurnitureCatalogTiles } from '../furnitureApi';
import { showAlert } from '../../../utils/telegram/alert';
import useFurniturePreviewStore from '../../../stores/useFurniturePreviewStore';
import type { RoomSceneState } from '../state/roomSceneState';
import type { MutableRefObject } from 'react';

interface AdminTilePanelProps {
  mapObjectId: number;
  adminTileMode: 'footprint' | 'surface';
  adminSelectedTiles: Set<string>;
  adminTileSaving: boolean;
  setAdminTileSaving: (saving: boolean) => void;
  adminTileCatalogId: number | null;
  setAdminTileMode: (mode: 'footprint' | 'surface' | null) => void;
  setAdminTileCatalogId: (id: number | null) => void;
  setAdminSelectedTiles: (tiles: Set<string>) => void;
  sceneRef: MutableRefObject<RoomSceneState>;
  getRoomViewport: () => Viewport | null;
}

/** Admin tile selection mode panel (footprint / surface editor: zoom, save, cancel). */
export default function AdminTilePanel({
  mapObjectId,
  adminTileMode,
  adminSelectedTiles,
  adminTileSaving,
  setAdminTileSaving,
  adminTileCatalogId,
  setAdminTileMode,
  setAdminTileCatalogId,
  setAdminSelectedTiles,
  sceneRef,
  getRoomViewport,
}: AdminTilePanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { previewItems, updatePreviewCatalog } = useFurniturePreviewStore();

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(0,0,0,0.85)',
        borderRadius: 16,
        padding: '12px 20px',
        border: `2px solid ${adminTileMode === 'footprint' ? '#ffa500' : '#8a2be2'}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
        {adminTileMode === 'footprint' ? '🟠 Edit Footprint' : '🟣 Edit Surface'}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginBottom: 4 }}>
        Tap tiles to toggle • {adminSelectedTiles.size} tiles selected
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {/* Zoom buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginRight: 4 }}>
          <button
            onClick={() => getRoomViewport()?.zoomPercent(0.25, true)}
            title="Zoom in"
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px 6px 0 0',
              padding: '3px 10px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >+</button>
          <button
            onClick={() => getRoomViewport()?.zoomPercent(-0.2, true)}
            title="Zoom out"
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '0 0 6px 6px',
              padding: '3px 10px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >−</button>
        </div>
        <button
          onClick={async () => {
            if (!adminTileCatalogId) return;
            setAdminTileSaving(true);
            try {
              const isSaveMirrored = sceneRef.current.admin.tileItemIsMirrored;
              const tiles = Array.from(adminSelectedTiles).map((key) => {
                const [dx, dy] = key.split(',').map(Number);
                // Reverse the visual mirror transform: editor shows (dy,dx) for mirrored items,
                // so swap back to canonical (dx,dy) before persisting to DB.
                const [cdx, cdy] = isSaveMirrored ? [dy, dx] : [dx, dy];
                return [cdx, cdy] as [number, number];
              });
              await updateFurnitureCatalogTiles(
                adminTileCatalogId,
                adminTileMode === 'footprint' ? tiles : null,
                adminTileMode === 'surface' ? tiles : null,
                adminTileMode === 'footprint' && tiles.length === 0,
                adminTileMode === 'surface' && tiles.length === 0,
              );
              // For preview items: update the store's catalog snapshot so the
              // live sprite gets fresh footprint/surface data on next render
              // (preview items are NOT refetched from backend, so store must be patched).
              const previewItem = previewItems.find(p => p.catalogId === adminTileCatalogId);
              if (previewItem) {
                const savedTiles = tiles.length > 0 ? tiles : null;
                updatePreviewCatalog(previewItem.instanceId, {
                  ...previewItem.furniture,
                  footprint_tiles: adminTileMode === 'footprint' ? savedTiles : previewItem.furniture.footprint_tiles,
                  surface_tiles: adminTileMode === 'surface' ? savedTiles : previewItem.furniture.surface_tiles,
                });
              }
              // Patch catalog cache directly so the shop shows fresh data immediately
              // (invalidate alone would only refetch when the shop is actively mounted).
              const savedFp = adminTileMode === 'footprint' ? (tiles.length > 0 ? tiles : null) : undefined;
              const savedSf = adminTileMode === 'surface' ? (tiles.length > 0 ? tiles : null) : undefined;
              const patchCatalog = (old: FurnitureCatalog[] | undefined) =>
                old?.map(item => item.id === adminTileCatalogId ? {
                  ...item,
                  ...(savedFp !== undefined && { footprint_tiles: savedFp }),
                  ...(savedSf !== undefined && { surface_tiles: savedSf }),
                } : item);
              for (const btype of ['house', 'cityhall'] as const) {
                queryClient.setQueryData<FurnitureCatalog[]>(['furnitureCatalog', btype], patchCatalog);
              }
              queryClient.invalidateQueries({ queryKey: ['houseFurniture', mapObjectId] });
              queryClient.invalidateQueries({ queryKey: ['furnitureCatalog'] });
              setAdminTileMode(null);
              setAdminTileCatalogId(null);
              setAdminSelectedTiles(new Set());
              sceneRef.current.admin.tileCenter = null;
            } catch (err) {
              showAlert(t('Failed to save tiles. Please try again.'));
              throw err;
            } finally {
              setAdminTileSaving(false);
            }
          }}
          disabled={adminTileSaving}
          style={{
            background: adminTileMode === 'footprint' ? '#ffa500' : '#8a2be2',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '8px 20px',
            fontSize: 14,
            fontWeight: 700,
            cursor: adminTileSaving ? 'not-allowed' : 'pointer',
            opacity: adminTileSaving ? 0.6 : 1,
          }}
        >
          {adminTileSaving ? '...' : '✓ Save'}
        </button>
        <button
          onClick={() => {
            setAdminTileMode(null);
            setAdminTileCatalogId(null);
            setAdminSelectedTiles(new Set());
            sceneRef.current.admin.tileCenter = null;
          }}
          disabled={adminTileSaving}
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 10,
            padding: '8px 20px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ✕ Cancel
        </button>
      </div>
    </div>
  );
}
